import { useMemo, useRef, useState, type Dispatch } from "react";
import { useForm, useWatch, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { fieldErrorsOf } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import type { PublicBookingChannel } from "../../channel";
import type { BookingConfig, VenueConfig } from "../../config/types";
import { Field } from "../components/Field";
import { HoldCountdown } from "../components/HoldCountdown";
import { ChallengeInterstitial, classifyAbuseProblem } from "../challenge";
import { formatServiceDate, formatSlotTime, wallClockToEpochSec } from "../format";
import { bookingRejectionOf, createBooking, type CreateBookingBody } from "../publicApi";
import { classifyRejection } from "../rejection";
import { guestDetailsSchema, orUndefined, type GuestDetailsValues } from "../schema";
import type { Confirmation, FlowAction, FlowState } from "../flow/state";

type DetailsState = Extract<FlowState, { step: "DETAILS" }>;

/** Server field name → form field (ADR-0008 mapping). Unknown fields land on the given name. */
function mapServerField(field: string): keyof GuestDetailsValues {
  const f = field.toLowerCase();
  if (f.includes("email")) return "email";
  if (f.includes("phone") || f.includes("tel")) return "phone";
  if (f.includes("family")) return "familyName";
  if (f.includes("zone")) return "zonePreference";
  if (f.includes("terms")) return "termsAccepted";
  if (f.includes("allerg")) return "allergyNote";
  return "givenName";
}

/**
 * DETAILS: guest checkout. Client validation is R4's floor only (schema.ts) — the server owns
 * plausibility (accept-and-flag). The hold countdown (instant path) is server-authoritative and its
 * expiry swaps to fresh alternatives (R7). The allergy note is its OWN field; the terms panel + gate
 * checkbox block submission until acknowledged (R3). One idempotency key per submission (BK-2),
 * reminted after a failed attempt so the corrected retry is a fresh logical write.
 */
export function Details({
  state,
  dispatch,
  config,
  booking,
  channel,
  locale,
  preview = false,
}: {
  state: DetailsState;
  dispatch: Dispatch<FlowAction>;
  config: VenueConfig;
  booking: BookingConfig;
  channel: PublicBookingChannel;
  locale: string;
  /** Preview/test mode: simulate the submit → a synthetic confirmation, never a real create call. */
  preview?: boolean;
}) {
  const { t } = useTranslation();
  const schema = useMemo(
    () => guestDetailsSchema(t, { requestVariant: state.requestVariant }),
    [t, state.requestVariant],
  );
  const form = useForm<GuestDetailsValues>({
    resolver: zodResolver(schema) as Resolver<GuestDetailsValues>,
    defaultValues: {
      givenName: "",
      familyName: "",
      phone: "",
      email: "",
      occasion: "",
      specialRequests: "",
      allergyNote: "",
      zonePreference: "",
      preferredTime: "",
      termsAccepted: false,
    },
    mode: "onSubmit",
  });
  // `useWatch` (not `form.watch()`) — the RHF-recommended, React-Compiler-friendly way to subscribe to a
  // single field; `.watch()` returns a fresh, unmemoizable function each call (react(incompatible-library)).
  const termsChecked = useWatch({ control: form.control, name: "termsAccepted" });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // booking-abuse-protection: consent-free widget telemetry (no identifier, nothing cross-site) + the
  // escalation-only challenge interstitial. `mountedAt` feeds form-fill timing; `interacted` is the
  // interaction bit; `honeypotRef` is a field a human never sees.
  // Lazy ref init (React's own sanctioned exception to "no ref writes during render"): `useRef(Date.now())`
  // would call the impure `Date.now()` on EVERY render even though only the first call's result is ever
  // kept; guarding the write on the ref still being unset runs it exactly once. The static lint can't
  // prove the guard makes this single-execution, so it still flags the call itself.
  const mountedAt = useRef<number | null>(null);
  // oxlint-disable-next-line react/purity -- see the lazy-ref-init comment above.
  if (mountedAt.current === null) mountedAt.current = Date.now();
  const interacted = useRef(false);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const [challenge, setChallenge] = useState<{
    sitekey: string;
    retryable: boolean;
    body: CreateBookingBody;
  } | null>(null);

  const err = (name: keyof GuestDetailsValues): string | undefined =>
    form.formState.errors[name]?.message as string | undefined;

  /**
   * One create attempt (shared by the first submit AND the resubmit-with-token): create → dispatch on
   * success; on failure, the abuse guard's CHALLENGE_REQUIRED swaps in the interstitial, TEMPORARILY_BLOCKED
   * routes to the contact fallback, and every other typed rejection keeps its existing behaviour.
   */
  const attempt = async (body: CreateBookingBody, serviceDay: string): Promise<void> => {
    try {
      const res = await createBooking(state.search.venueId, state.idempotencyKey, body);
      setChallenge(null);
      const confirmation: Confirmation = {
        status: res.status === "CONFIRMED" ? "CONFIRMED" : "PENDING",
        bookingId: res.bookingId,
        venueId: state.search.venueId,
        manageToken: res.manageToken,
        email: body.guest.email,
        serviceId: res.serviceId,
        startSec: res.startSec,
        durationSec: res.durationSec,
        partySize: res.partySize,
      };
      // Card-guarantee gate (card-fingerprint-no-show.md, R7): a guarantee-required booking is created
      // but held — it cannot reach CONFIRMED/PENDING until the SetupIntent card step succeeds.
      if (res.guarantee?.required) {
        dispatch({
          type: "REQUIRE_GUARANTEE",
          guarantee: {
            venueId: state.search.venueId,
            bookingId: res.bookingId,
            feeMinor: res.guarantee.feeMinor,
            currency: res.guarantee.currency,
            cutoffAtSec: res.guarantee.cutoffAtSec ?? null,
            policyVersion: booking.policyVersion,
          },
          confirmation,
        });
        return;
      }
      dispatch(
        confirmation.status === "CONFIRMED"
          ? { type: "SUBMIT_CONFIRMED", confirmation }
          : { type: "SUBMIT_PENDING", confirmation },
      );
    } catch (error) {
      const abuse = classifyAbuseProblem(error);
      if (abuse?.kind === "challenge") {
        setChallenge({ sitekey: abuse.sitekey, retryable: abuse.retryable, body });
        return;
      }
      if (abuse?.kind === "blocked") {
        setChallenge(null);
        return dispatch({ type: "CONTACT_FALLBACK", rejectionCode: "TEMPORARILY_BLOCKED" });
      }
      const fieldErrors = Object.entries(fieldErrorsOf(error));
      if (fieldErrors.length > 0) {
        setChallenge(null);
        for (const [field, message] of fieldErrors) {
          form.setError(mapServerField(field), { type: "server", message });
        }
        // The corrected resubmit is a new logical write — remint the key (BK-2).
        dispatch({ type: "REMINT", idempotencyKey: crypto.randomUUID() });
        return;
      }
      const rej = bookingRejectionOf(error, serviceDay);
      if (rej) {
        const kind = classifyRejection(rej.rejectionCode).kind;
        if (kind === "alternatives") return dispatch({ type: "REJECT_TO_SLOTS" });
        if (kind === "blocked") return dispatch({ type: "BANNED" });
        if (kind === "contact")
          return dispatch({ type: "CONTACT_FALLBACK", rejectionCode: rej.rejectionCode });
      }
      setSubmitError(t("widget.booking.err.submitFailed"));
      dispatch({ type: "REMINT", idempotencyKey: crypto.randomUUID() });
    }
  };

  // react-hook-form's `handleSubmit` reads its internal refs synchronously when called; this is RHF's
  // standard, documented usage and not fixable locally (a known react-hook-form / React-Compiler
  // incompatibility — see the `useWatch` note above for the sibling case).
  // oxlint-disable-next-line react/refs -- see comment above.
  const submit = form.handleSubmit(async (data) => {
    setSubmitError(null);
    const serviceDay = state.search.serviceDay ?? "";
    const startSec = state.requestVariant
      ? wallClockToEpochSec(serviceDay, data.preferredTime, config.timeZone)
      : (state.slot?.startSec ?? 0);
    const serviceId = state.requestVariant
      ? booking.defaultServiceId
      : (state.slot?.serviceId ?? booking.defaultServiceId);

    const occasionLabel = t("widget.booking.details.occasion");
    const specialParts: string[] = [];
    if (data.occasion.trim()) specialParts.push(`${occasionLabel}: ${data.occasion.trim()}`);
    if (data.specialRequests.trim()) specialParts.push(data.specialRequests.trim());

    const body: CreateBookingBody = {
      serviceId,
      startSec,
      partySize: state.search.partySize,
      holdId: state.hold?.holdId ?? null,
      guest: {
        givenName: data.givenName.trim(),
        familyName: orUndefined(data.familyName),
        phone: orUndefined(data.phone),
        email: orUndefined(data.email),
      },
      termsAccepted: true,
      channel,
      zonePreference: orUndefined(data.zonePreference) ?? null,
      specialRequests: specialParts.length > 0 ? specialParts.join(" — ") : null,
      allergyNote: orUndefined(data.allergyNote) ?? null,
      language: locale,
      // booking-abuse-protection: consent-free signals (no identifier). The honeypot is read from the
      // hidden field a human never fills; timing + the interaction bit are the submission-speed signal.
      // Runs at submit time (inside RHF's handleSubmit callback), never during render; flagged only
      // because the callback itself is flagged, see the `submit` comment above.
      // oxlint-disable-next-line react/purity -- see comment above.
      formFillMillis: Date.now() - (mountedAt.current ?? Date.now()),
      honeypot: honeypotRef.current?.value || undefined,
      interacted: interacted.current,
    };

    // Preview/test mode: never POST a real booking — synthesize the confirmation the diner would see so
    // a manager can walk the whole flow to its end screen without creating a reservation. A
    // request-to-book service (or the large-party request variant) lands on PENDING, else CONFIRMED.
    if (preview) {
      const status =
        state.requestVariant || booking.bookingMode === "REQUEST_TO_BOOK" ? "PENDING" : "CONFIRMED";
      const confirmation: Confirmation = {
        status,
        bookingId: "preview",
        venueId: state.search.venueId,
        manageToken: "preview",
        email: body.guest.email,
        serviceId,
        startSec,
        durationSec: state.slot?.durationSec ?? 0,
        partySize: state.search.partySize,
      };
      dispatch(
        status === "CONFIRMED"
          ? { type: "SUBMIT_CONFIRMED", confirmation }
          : { type: "SUBMIT_PENDING", confirmation },
      );
      return;
    }

    await attempt(body, serviceDay);
  });

  const slotSummary = state.requestVariant
    ? t("widget.booking.details.requestSummary", {
        date: formatServiceDate(state.search.serviceDay ?? "", config.timeZone, locale),
        guests: state.search.partySize,
      })
    : t("widget.booking.details.slotSummary", {
        date: formatServiceDate(state.search.serviceDay ?? "", config.timeZone, locale),
        time: formatSlotTime(state.slot?.startSec ?? 0, config.timeZone, locale),
        guests: state.search.partySize,
      });

  // Escalation-only (R1/R5): the challenge interstitial replaces the form ONLY after the guard returns
  // CHALLENGE_REQUIRED; the happy path never renders it and never loads the Turnstile script.
  if (challenge) {
    return (
      <ChallengeInterstitial
        sitekey={challenge.sitekey}
        retryable={challenge.retryable}
        language={locale}
        onToken={(token) => {
          void attempt({ ...challenge.body, challengeToken: token }, state.search.serviceDay ?? "");
        }}
        onRetry={() => setChallenge(null)}
        labels={{
          prompt: t("widget.booking.challenge.prompt"),
          retry: t("widget.booking.challenge.retry"),
          retryCta: t("widget.booking.challenge.retryCta"),
        }}
      />
    );
  }

  return (
    <form
      className="rsv-step"
      onSubmit={(e) => void submit(e)}
      onChange={() => {
        interacted.current = true;
      }}
      aria-label={t("widget.booking.details.title")}
    >
      {/* Honeypot: hidden from humans; any value marks a bot (booking-abuse-protection). Not a form field. */}
      <input
        ref={honeypotRef}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <h2>
        {state.requestVariant
          ? t("widget.booking.details.requestTitle")
          : t("widget.booking.details.title")}
      </h2>
      <p className="rsv-summary">{slotSummary}</p>

      {state.hold ? (
        <HoldCountdown
          expiresAtSec={state.hold.expiresAtSec}
          onExpire={() => dispatch({ type: "HOLD_EXPIRED" })}
        />
      ) : null}

      {state.requestVariant ? (
        <Field
          id="rsv-time"
          label={t("widget.booking.details.preferredTime")}
          error={err("preferredTime")}
        >
          {(aria) => (
            <input
              {...aria}
              {...form.register("preferredTime")}
              className="rsv-input"
              type="time"
            />
          )}
        </Field>
      ) : null}

      <Field id="rsv-given" label={t("widget.booking.details.givenName")} error={err("givenName")}>
        {(aria) => <input {...aria} {...form.register("givenName")} className="rsv-input" />}
      </Field>
      <Field
        id="rsv-family"
        label={t("widget.booking.details.familyName")}
        error={err("familyName")}
      >
        {(aria) => <input {...aria} {...form.register("familyName")} className="rsv-input" />}
      </Field>
      <Field
        id="rsv-phone"
        label={t("widget.booking.details.phone")}
        hint={t("widget.booking.details.phoneHint")}
        error={err("phone")}
      >
        {(aria) => (
          <input
            {...aria}
            {...form.register("phone")}
            className="rsv-input"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
          />
        )}
      </Field>
      <Field
        id="rsv-email"
        label={t("widget.booking.details.email")}
        hint={t("widget.booking.details.contactHint")}
        error={err("email")}
      >
        {(aria) => (
          <input
            {...aria}
            {...form.register("email")}
            className="rsv-input"
            type="email"
            autoComplete="email"
            inputMode="email"
          />
        )}
      </Field>

      {booking.zones.length > 0 ? (
        <fieldset className="rsv-fieldset">
          <legend className="rsv-legend">{t("widget.booking.details.zone")}</legend>
          <label className="rsv-radio">
            <input type="radio" value="" {...form.register("zonePreference")} defaultChecked />
            {t("widget.booking.details.zoneAny")}
          </label>
          {booking.zones.map((zone) => (
            <label key={zone.zoneId} className="rsv-radio">
              <input type="radio" value={zone.zoneId} {...form.register("zonePreference")} />
              {zone.name}
            </label>
          ))}
        </fieldset>
      ) : null}

      <Field id="rsv-occasion" label={t("widget.booking.details.occasion")} error={err("occasion")}>
        {(aria) => <input {...aria} {...form.register("occasion")} className="rsv-input" />}
      </Field>
      <Field
        id="rsv-requests"
        label={t("widget.booking.details.requests")}
        error={err("specialRequests")}
      >
        {(aria) => (
          <textarea
            {...aria}
            {...form.register("specialRequests")}
            className="rsv-textarea"
            rows={2}
          />
        )}
      </Field>
      <Field
        id="rsv-allergy"
        label={t("widget.booking.details.allergy")}
        hint={t("widget.booking.details.allergyHint")}
        error={err("allergyNote")}
      >
        {(aria) => (
          <textarea {...aria} {...form.register("allergyNote")} className="rsv-textarea" rows={2} />
        )}
      </Field>

      <div>
        <h3 id="rsv-terms-heading">{t("widget.booking.details.termsHeading")}</h3>
        <section className="rsv-terms" aria-labelledby="rsv-terms-heading">
          {booking.termsText || t("widget.booking.details.termsFallback")}
        </section>
      </div>
      <label className="rsv-check">
        <input type="checkbox" {...form.register("termsAccepted")} />
        <span>{t("widget.booking.details.termsAck")}</span>
      </label>
      {err("termsAccepted") ? (
        <span className="rsv-error" role="alert">
          {err("termsAccepted")}
        </span>
      ) : null}

      {submitError ? (
        <p className="rsv-notice" role="alert">
          {submitError}
        </p>
      ) : null}

      <div className="rsv-actions">
        <button
          type="submit"
          className="rsv-button"
          disabled={!termsChecked || form.formState.isSubmitting}
        >
          {state.requestVariant
            ? t("widget.booking.details.sendRequest")
            : t("widget.booking.details.confirm")}
        </button>
        <button type="button" className="rsv-secondary" onClick={() => dispatch({ type: "BACK" })}>
          {t("widget.booking.back")}
        </button>
      </div>
    </form>
  );
}
